/**
 * Piezas que comparten las pestañas de Reportes.
 *
 * Vivían en `reports.page.tsx` junto con todo lo demás —2.076 líneas, ocho
 * unidades— y eso hacía que cualquier cambio en una pestaña obligara a navegar
 * el archivo entero. Se extraen tal cual estaban: el objetivo es dónde vive el
 * código, no cómo funciona.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { formatMoney } from '../../../../lib/i18n-format';

/** Monto en la moneda del tenant, con formato chileno. */
export function fmt(amount: number, currency = 'CLP'): string {
  return formatMoney(amount, currency);
}

export function pct(value: number): string {
  return `${value}%`;
}

export const PLAN_STATUS_COLOR: Record<
  string,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  active: 'success',
  completed: 'info',
  defaulted: 'error',
  withdrawn: 'default',
};

/**
 * Window-virtualizes a long table body via spacer rows. Keeps the real
 * `<Table>` (and its responsive `<TableCell>`s) intact — only the on-screen
 * window of rows is mounted; a top + bottom spacer `<TableRow>` reserves the
 * scroll height for the rest. Scrolls against the window, so the page keeps its
 * natural scroll. `containerRef` goes on the `<TableContainer>` (its `offsetTop`
 * is the table's document position).
 */
export function useTableWindowVirtualizer(count: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    const top = containerRef.current?.offsetTop ?? 0;
    setOffset((prev) => (prev === top ? prev : top));
  }, [count]);
  const virtualizer = useWindowVirtualizer({
    count,
    estimateSize: () => 53,
    overscan: 10,
    scrollMargin: offset,
  });
  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = items.length > 0 ? items[0]!.start - offset : 0;
  const paddingBottom = items.length > 0 ? totalSize - items[items.length - 1]!.end + offset : 0;
  return { containerRef, virtualizer, items, paddingTop, paddingBottom };
}
